import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';

interface JamSessionControlsProps {
  sessionState?: 'idle' | 'hosting' | 'joining' | 'connected' | 'fallback-server';
  sessionRole?: 'host' | 'client' | null;
  sessionCode?: string;
  connectedClients?: string[];
  onStateChange?: (state: 'idle' | 'hosting' | 'joining' | 'connected' | 'fallback-server') => void;
  onRoleChange?: (role: 'host' | 'client' | null) => void;
  onCodeChange?: (code: string) => void;
  onLeaveSession?: () => void;
  forceExpanded?: boolean; // when true, skip collapsed view (banner IS the collapsed state)
  onClose?: () => void;   // called when user clicks Minimize in overlay context
}

// For now, we'll generate a simple session code
const generateSessionCode = (): string => {
  return Math.random().toString(36).substr(2, 6).toUpperCase();
};

const JamSessionControls: React.FC<JamSessionControlsProps> = React.memo(({
  sessionState: externalSessionState,
  sessionRole: externalSessionRole,
  sessionCode: externalSessionCode,
  connectedClients: externalConnectedClients,
  onStateChange,
  onRoleChange,
  onCodeChange,
  onLeaveSession,
  forceExpanded = false,
  onClose,
}) => {
    // Use external state if provided, otherwise use internal state
    const [internalSessionState, setInternalSessionState] = useState<'idle' | 'hosting' | 'joining' | 'connected'>('idle');
    const [internalSessionCode, setInternalSessionCode] = useState<string>('');
    const [joinCode, setJoinCode] = useState<string>('');
    const [qrCodeDataURL, setQrCodeDataURL] = useState<string>('');
    
    // Manage collapsed state internally with localStorage persistence
    const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
        // Initialize from localStorage if available
        const saved = localStorage.getItem('jamSessionControlsCollapsed');
        return saved ? JSON.parse(saved) : false;
    });

    // Save collapsed state to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('jamSessionControlsCollapsed', JSON.stringify(isCollapsed));
    }, [isCollapsed]);

    const sessionState = externalSessionState ?? internalSessionState;
    const sessionRole = externalSessionRole ?? null;
    const sessionCode = externalSessionCode ?? internalSessionCode;
    const connectedClients = externalConnectedClients ?? [];

    // Generate QR code when session code changes
    useEffect(() => {
        if (sessionCode && (sessionState === 'hosting' || sessionState === 'connected' || sessionState === 'fallback-server')) {
            // Create a join URL or just the session code
            const qrData = `${window.location.origin}?join=${sessionCode}`;
            QRCode.toDataURL(qrData, {
                width: 128,
                margin: 1,
                color: {
                    dark: '#1f2937',  // Gray-800
                    light: '#ffffff'
                }
            })
            .then(url => {
                setQrCodeDataURL(url);
            })
            .catch(err => {
                console.error('Error generating QR code:', err);
            });
        } else {
            setQrCodeDataURL('');
        }
    }, [sessionCode, sessionState]);

    const updateState = (newState: 'idle' | 'hosting' | 'joining' | 'connected') => {
        if (onStateChange) {
            onStateChange(newState);
        } else {
            setInternalSessionState(newState);
        }
    };

    const updateCode = (newCode: string) => {
        if (onCodeChange) {
            onCodeChange(newCode);
        } else {
            setInternalSessionCode(newCode);
        }
    };

    const handleCreateSession = () => {
        const code = generateSessionCode();
        updateCode(code);
        updateState('hosting');
        if (onRoleChange) onRoleChange('host');
        // Future: Initialize WebRTC host logic here
        console.log('Creating jam session with code:', code);
    };

    const handleJoinSession = () => {
        if (!joinCode.trim()) {
            alert('Please enter a session code');
            return;
        }
        updateState('joining');
        updateCode(joinCode);
        if (onRoleChange) onRoleChange('client');
        // Future: Initialize WebRTC client logic here
        console.log('Joining jam session with code:', joinCode);
        // Simulate successful connection for now
        setTimeout(() => updateState('connected'), 1000);
    };

    const handleEndSession = () => {
        // First notify the server/peers via WebRTC service
        if (onLeaveSession) {
            onLeaveSession();
        }
        // Then update local state
        updateState('idle');
        updateCode('');
        setJoinCode('');
        if (onRoleChange) onRoleChange(null);
        console.log('🔴 Ending jam session');
    };

    const handleLeaveSession = () => {
        // First notify the server/peers via WebRTC service
        if (onLeaveSession) {
            onLeaveSession();
        }
        // Then update local state
        updateState('idle');
        setJoinCode('');
        if (onRoleChange) onRoleChange(null);
        console.log('👋 Leaving jam session');
    };

    // Initial screen - choose to host or join
    if (sessionState === 'idle') {
        return (
            <div 
                className="w-full max-w-lg mx-auto p-4 sm:p-8 rounded-lg my-4 shadow-lg flex flex-col items-center gap-4 sm:gap-6 text-center"
                style={{ backgroundColor: '#161616' }}
                data-debug-id="Level-1:JamSessionControls"
            >
                <h2 className="text-2xl sm:text-3xl font-bold text-[#E4393F]">🎵 Jam Session</h2>
                <p className="text-gray-300 text-sm sm:text-base">
                    Share your chart in real-time with other musicians
                </p>
                
                <div className="flex flex-col gap-3 w-full mt-2 sm:mt-4">
                    <button
                        onClick={handleCreateSession}
                        className="w-full px-4 sm:px-6 py-3 rounded-lg bg-[#E4393F] hover:bg-[#E4393F]/80 text-white font-semibold transition-all shadow-md focus:outline-none focus:ring-2 focus:ring-[#E4393F] focus:ring-offset-2 focus:ring-offset-gray-800 text-base sm:text-lg flex items-center justify-center"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6 mr-2 sm:mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12s-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                        </svg>
                        Host Session
                    </button>
                    
                    <div className="w-full flex flex-col gap-2">
                        <input
                            type="text"
                            value={joinCode}
                            onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                            placeholder="Enter session code"
                            className="w-full px-3 sm:px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent text-center font-mono text-base sm:text-lg"
                            maxLength={6}
                        />
                        <button
                            onClick={handleJoinSession}
                            className="w-full px-4 sm:px-6 py-2 sm:py-3 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white font-semibold transition-all shadow-md focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-gray-800 text-base"
                        >
                            Join Session
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Hosting screen - show session code and connected clients (for hosts)
    if ((sessionState === 'hosting' || sessionState === 'connected' || sessionState === 'fallback-server') && sessionRole === 'host') {
        // Collapsed view bypassed when forceExpanded (banner IS the collapsed state)
        if (isCollapsed && !forceExpanded) {
            return (
                <div 
                    className="w-full mx-auto bg-gray-800/90 backdrop-blur-sm rounded-lg my-1 shadow-md border border-gray-700"
                    data-debug-id="Level-1:JamSessionControls-Hosting-Collapsed"
                >
                    <div className="flex items-center justify-between px-3 py-2 overflow-hidden">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-shrink-0">
                                <div className="w-2 h-2 rounded-full bg-green-400"></div>
                                <span className="text-[#E4393F] font-medium text-sm">Hosting</span>
                                <svg width="14" height="16" viewBox="0 0 20 22" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-4">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M7.68328 15.4992C9.33607 15.6962 10.5861 16.1886 11.5855 16.8168C11.5613 16.9992 11.5487 17.1853 11.5487 17.3742C11.5487 19.6884 13.4248 21.5645 15.739 21.5645C18.0532 21.5645 19.9293 19.6884 19.9293 17.3742C19.9293 15.06 18.0532 13.184 15.739 13.184C14.2181 13.184 12.8864 13.9943 12.1522 15.2068C10.7063 14.8682 9.45636 14.2932 8.37264 13.4429C8.37786 13.3572 8.38052 13.2708 8.38052 13.1838C8.38052 12.3308 8.12565 11.5374 7.68791 10.8754C8.61427 8.99638 9.71798 8.07426 10.8663 7.53689C11.568 8.06648 12.4415 8.3805 13.3883 8.3805C15.7025 8.3805 17.5785 6.50446 17.5785 4.19025C17.5785 1.87604 15.7025 0 13.3883 0C11.0741 0 9.19802 1.87604 9.19802 4.19025C9.19802 4.83544 9.34383 5.44657 9.60431 5.99249C8.55594 7.57688 7.31726 8.6129 5.5697 9.22593C5.13772 9.07539 4.67355 8.99357 4.19026 8.99357C1.87604 8.99357 0 10.8696 0 13.1838C0 15.498 1.87604 17.3741 4.19026 17.3741C5.64849 17.3741 6.93274 16.6292 7.68328 15.4992Z" fill="#E4393F"/>
                                </svg>
                            </div>
                            <div className="flex items-center gap-2 text-xs min-w-0">
                                <span className="text-gray-400 flex-shrink-0">Code:</span>
                                <span className="font-mono text-purple-300 tracking-wider font-medium">{sessionCode}</span>
                                <button
                                    onClick={() => navigator.clipboard.writeText(sessionCode)}
                                    className="hidden sm:inline-flex px-1.5 py-0.5 bg-[#E4393F]/20 hover:bg-[#E4393F]/40 text-[#FFC2C4] rounded text-xs transition-colors flex-shrink-0"
                                    title="Copy session code"
                                >
                                    Copy
                                </button>
                            </div>
                            <div className="flex items-center gap-1.5 text-xs flex-shrink-0">
                                <span className="text-gray-400">Musicians:</span>
                                <span className="text-green-400 font-medium">{connectedClients.length}</span>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                            <button
                                onClick={handleEndSession}
                                className="px-2 py-1 bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded text-xs font-medium transition-colors"
                            >
                                End
                            </button>
                            <button
                                onClick={() => setIsCollapsed(false)}
                                className="px-2 py-1 bg-gray-600 hover:bg-gray-500 text-white rounded text-xs font-medium transition-colors flex items-center gap-1"
                                title="Expand jam session controls"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                                <span className="hidden sm:inline">Expand</span>
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        // Expanded view - full panel (existing layout with collapse button)
        return (
            <div 
                className={`w-full max-w-2xl mx-auto p-4 sm:p-8 rounded-lg shadow-lg flex flex-col items-center gap-4 sm:gap-6 ${forceExpanded ? 'my-0' : 'my-4'}`}
                style={{ backgroundColor: '#161616' }}
                data-debug-id="Level-1:JamSessionControls-Hosting-Expanded"
            >
                <div className="text-center relative w-full">
                    <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2">
                        <h2 className="text-xl sm:text-3xl font-bold text-[#E4393F]">🎵 Hosting</h2>
                        <svg width="16" height="18" viewBox="0 0 20 22" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-5 h-6 sm:w-6 sm:h-7">
                            <path fillRule="evenodd" clipRule="evenodd" d="M7.68328 15.4992C9.33607 15.6962 10.5861 16.1886 11.5855 16.8168C11.5613 16.9992 11.5487 17.1853 11.5487 17.3742C11.5487 19.6884 13.4248 21.5645 15.739 21.5645C18.0532 21.5645 19.9293 19.6884 19.9293 17.3742C19.9293 15.06 18.0532 13.184 15.739 13.184C14.2181 13.184 12.8864 13.9943 12.1522 15.2068C10.7063 14.8682 9.45636 14.2932 8.37264 13.4429C8.37786 13.3572 8.38052 13.2708 8.38052 13.1838C8.38052 12.3308 8.12565 11.5374 7.68791 10.8754C8.61427 8.99638 9.71798 8.07426 10.8663 7.53689C11.568 8.06648 12.4415 8.3805 13.3883 8.3805C15.7025 8.3805 17.5785 6.50446 17.5785 4.19025C17.5785 1.87604 15.7025 0 13.3883 0C11.0741 0 9.19802 1.87604 9.19802 4.19025C9.19802 4.83544 9.34383 5.44657 9.60431 5.99249C8.55594 7.57688 7.31726 8.6129 5.5697 9.22593C5.13772 9.07539 4.67355 8.99357 4.19026 8.99357C1.87604 8.99357 0 10.8696 0 13.1838C0 15.498 1.87604 17.3741 4.19026 17.3741C5.64849 17.3741 6.93274 16.6292 7.68328 15.4992Z" fill="#E4393F"/>
                        </svg>
                    </div>
                    <p className="text-gray-300 text-sm sm:text-base">Share this code with other musicians</p>
                    
                    {/* Collapse Button — hidden when forceExpanded (banner's arrow handles closing) */}
                    {!forceExpanded && (
                    <button
                        onClick={() => { setIsCollapsed(true); onClose?.(); }}
                        className="absolute top-0 right-0 px-2 sm:px-3 py-1 sm:py-1.5 bg-gray-600 hover:bg-gray-500 text-white rounded-lg text-xs sm:text-sm font-medium transition-colors flex items-center gap-1"
                        title="Minimize jam session controls"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                        <span className="hidden sm:inline">Minimize</span>
                    </button>
                    )}
                </div>

                {/* Session Code Display */}
                <div className="bg-gray-900 p-4 sm:p-6 rounded-lg border-2 border-[#E4393F] text-center w-full">
                    <p className="text-gray-400 text-xs sm:text-sm mb-2">Session Code</p>
                    <div className="text-2xl sm:text-4xl font-mono font-bold text-[#FFC2C4] tracking-widest">
                        {sessionCode}
                    </div>
                    <button
                        onClick={() => navigator.clipboard.writeText(sessionCode)}
                        className="mt-3 px-3 sm:px-4 py-2 bg-[#E4393F] hover:bg-[#E4393F]/80 text-white rounded-lg text-xs sm:text-sm font-semibold transition-colors"
                    >
                        Copy Code
                    </button>
                </div>

                {/* QR Code */}
                <div className="bg-gray-900 p-3 sm:p-4 rounded-lg border border-gray-600">
                    <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-lg flex items-center justify-center bg-white mx-auto">
                        {qrCodeDataURL ? (
                            <img 
                                src={qrCodeDataURL} 
                                alt="QR Code for joining session" 
                                className="w-full h-full object-contain rounded"
                            />
                        ) : (
                            <span className="text-gray-500 text-xs text-center">
                                Generating<br/>QR Code...
                            </span>
                        )}
                    </div>
                    <p className="text-gray-400 text-xs text-center mt-2">
                        Scan to join session
                    </p>
                </div>

                {/* Connected Clients */}
                <div className="w-full">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-300 mb-2 sm:mb-3">
                        Connected Musicians ({connectedClients.length})
                    </h3>
                    <div className="bg-gray-900 rounded-lg p-3 sm:p-4 min-h-[80px] sm:min-h-[100px]">
                        {connectedClients.length === 0 ? (
                            <p className="text-gray-500 text-center py-6 sm:py-8 text-sm sm:text-base">Waiting for musicians to join...</p>
                        ) : (
                            <div className="space-y-2">
                                {connectedClients.map((client, index) => (
                                    <div key={index} className="flex items-center justify-between bg-gray-700 p-2 sm:p-3 rounded-lg">
                                        <span className="text-gray-300 text-sm sm:text-base">{client}</span>
                                        <span className="text-green-400 text-xs sm:text-sm">Connected</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Control Buttons */}
                <div className="flex gap-4 w-full sm:w-auto">
                    <button
                        onClick={handleEndSession}
                        className="flex-1 sm:flex-none px-4 sm:px-6 py-2 sm:py-3 bg-red-600 hover:bg-red-500 text-white rounded-lg font-semibold transition-colors text-sm sm:text-base"
                    >
                        End Session
                    </button>
                </div>
            </div>
        );
    }

    // Joining/Connected screen (for clients)
    if ((sessionState === 'joining' || sessionState === 'connected' || sessionState === 'fallback-server') && sessionRole === 'client') {
        return (
            <div 
                className="w-full max-w-lg mx-auto p-4 sm:p-8 rounded-lg my-4 shadow-lg flex flex-col items-center gap-4 sm:gap-6 text-center"
                style={{ backgroundColor: '#161616' }}
                data-debug-id="Level-1:JamSessionControls-Client"
            >
                <h2 className="text-2xl sm:text-3xl font-bold text-cyan-400">
                    {sessionState === 'joining' ? '🔄 Joining...' : (sessionState === 'fallback-server' ? '🔄 Connected (Server Mode)' : '🎵 Connected')}
                </h2>
                
                {sessionState === 'joining' ? (
                    <p className="text-gray-300 text-sm sm:text-base">Connecting to session {joinCode}...</p>
                ) : (
                    <div className="text-center">
                        <p className="text-gray-300 mb-2 text-sm sm:text-base">Connected to session</p>
                        <div className="text-lg sm:text-xl font-mono font-bold text-cyan-300">{joinCode}</div>
                        <p className="text-gray-400 text-xs sm:text-sm mt-3">You are viewing the host's chart in read-only mode</p>
                    </div>
                )}

                {(sessionState === 'connected' || sessionState === 'fallback-server') && (
                    <button
                        onClick={handleLeaveSession}
                        className="px-4 sm:px-6 py-2 sm:py-3 bg-gray-600 hover:bg-gray-500 text-white rounded-lg font-semibold transition-colors text-sm sm:text-base w-full sm:w-auto"
                    >
                        Leave Session
                    </button>
                )}
            </div>
        );
    }

    return null;
});

export default JamSessionControls;